"use strict";

import { of } from "rxjs";
import { ajax } from "rxjs/ajax";
import { ofType } from "redux-observable";
import { mergeMap, catchError, exhaustMap, map, switchMap } from "rxjs/operators";

import { requestTypes, retailSalesConstants } from "../_constants";
import { getRequestHeader } from "../../_helpers/request.header";
import {
     ajaxError,
     retailSalesAddWinnersComplete,
     retailSalesDealerListComplete,
     retailSalesDeleteJobTitleComplete,
     retailSalesGetJobTitlesComplete,
     retailSalesSaveJobTitlesComplete,
     retailSalesDealerLookupComplete,
     retailSalesGetDealersComplete,
     retailSalesSaveDealersComplete,
     retailSalesGetActivationReportComplete,
     retailSalesGetActivationReportExportComplete,
     retailSalesGetWinnerFilterOptionsComplete,
     retailSalesGetWinnerVouchersComplete,
     retailSalesGetWinnersComplete,
     retailSalesResendActivationComplete,
     retailSalesUpdateWinnerComplete,
} from "../_actions";

export const retailSalesGetWinnersEpic = (actions$) => {
     return actions$.pipe(
          ofType(retailSalesConstants.RETAIL_SALES_GET_WINNERS_REQUEST),
          switchMap((action) => {
               return ajax
                    .post(
                         "/api/retailSales/getWinners",
                         action.payload,
                         getRequestHeader(requestTypes.REQUEST_TYPE_POST),
                    )
                    .pipe(
                         map((ajaxResponse) => {
                              return retailSalesGetWinnersComplete(
                                   ajaxResponse.response,
                              );
                         }),
                         catchError((error) => of(ajaxError(error))),
                    );
          }),
     );
};

export const retailSalesGetActivationReportEpic = (actions$) => {
     return actions$.pipe(
          ofType(retailSalesConstants.RETAIL_SALES_GET_ACTIVATION_REPORT_REQUEST),
          switchMap((action) => {
               return ajax
                    .post(
                         "/api/retailSales/getActivationReport",
                         action.payload,
                         getRequestHeader(requestTypes.REQUEST_TYPE_POST),
                    )
                    .pipe(
                         map((ajaxResponse) => {
                              return retailSalesGetActivationReportComplete(
                                   ajaxResponse.response,
                              );
                         }),
                         catchError((error) => of(ajaxError(error))),
                    );
          }),
     );
};

export const retailSalesGetActivationReportExportEpic = (actions$) => {
     return actions$.pipe(
          ofType(retailSalesConstants.RETAIL_SALES_GET_ACTIVATION_REPORT_EXPORT_REQUEST),
          // exhaustMap, so a second click while the export is still running is ignored rather than
          // queued. The call returns every matching row and can be slow.
          exhaustMap((action) => {
               return ajax
                    .post(
                         "/api/retailSales/getActivationReportExport",
                         action.payload,
                         getRequestHeader(requestTypes.REQUEST_TYPE_POST),
                    )
                    .pipe(
                         map((ajaxResponse) => {
                              return retailSalesGetActivationReportExportComplete(
                                   ajaxResponse.response,
                              );
                         }),
                         catchError((error) => of(ajaxError(error))),
                    );
          }),
     );
};

export const retailSalesGetWinnerFilterOptionsEpic = (actions$) => {
     return actions$.pipe(
          ofType(
               retailSalesConstants.RETAIL_SALES_GET_WINNER_FILTER_OPTIONS_REQUEST,
          ),
          mergeMap((action) => {
               return ajax
                    .post(
                         "/api/retailSales/getWinnerFilterOptions",
                         action.payload,
                         getRequestHeader(requestTypes.REQUEST_TYPE_POST),
                    )
                    .pipe(
                         map((ajaxResponse) => {
                              return retailSalesGetWinnerFilterOptionsComplete(
                                   ajaxResponse.response,
                              );
                         }),
                         catchError((error) => of(ajaxError(error))),
                    );
          }),
     );
};

export const retailSalesGetWinnerVouchersEpic = (actions$) => {
     return actions$.pipe(
          ofType(retailSalesConstants.RETAIL_SALES_GET_WINNER_VOUCHERS_REQUEST),
          switchMap((action) => {
               return ajax
                    .post(
                         "/api/retailSales/getWinnerVouchers",
                         action.payload,
                         getRequestHeader(requestTypes.REQUEST_TYPE_POST),
                    )
                    .pipe(
                         map((ajaxResponse) => {
                              return retailSalesGetWinnerVouchersComplete(
                                   ajaxResponse.response,
                              );
                         }),
                         catchError((error) => of(ajaxError(error))),
                    );
          }),
     );
};

export const retailSalesResendActivationEpic = (actions$) => {
     return actions$.pipe(
          ofType(retailSalesConstants.RETAIL_SALES_RESEND_ACTIVATION_REQUEST),
          mergeMap((action) => {
               return ajax
                    .post(
                         "/api/retailSales/resendActivation",
                         action.payload,
                         getRequestHeader(requestTypes.REQUEST_TYPE_POST),
                    )
                    .pipe(
                         map((ajaxResponse) => {
                              return retailSalesResendActivationComplete(
                                   ajaxResponse.response,
                              );
                         }),
                         catchError((error) => of(ajaxError(error))),
                    );
          }),
     );
};

// Adding winners pays real points and emails the winner, so a second request while the first is
// still running would pay everyone twice. exhaustMap ignores anything dispatched until the request
// in flight has finished, which switchMap would not do: switchMap drops the response but the server
// has already been sent the work.
export const retailSalesAddWinnersEpic = (actions$) => {
     return actions$.pipe(
          ofType(retailSalesConstants.RETAIL_SALES_ADD_WINNERS_REQUEST),
          exhaustMap((action) => {
               return ajax
                    .post(
                         "/api/retailSales/addWinners",
                         action.payload,
                         getRequestHeader(requestTypes.REQUEST_TYPE_POST),
                    )
                    .pipe(
                         map((ajaxResponse) => {
                              return retailSalesAddWinnersComplete(
                                   ajaxResponse.response,
                              );
                         }),
                         catchError((error) => of(ajaxError(error))),
                    );
          }),
     );
};

export const retailSalesUpdateWinnerEpic = (actions$) => {
     return actions$.pipe(
          ofType(retailSalesConstants.RETAIL_SALES_UPDATE_WINNER_REQUEST),
          mergeMap((action) => {
               return ajax
                    .post(
                         "/api/retailSales/updateWinner",
                         action.payload,
                         getRequestHeader(requestTypes.REQUEST_TYPE_POST),
                    )
                    .pipe(
                         map((ajaxResponse) => {
                              return retailSalesUpdateWinnerComplete(
                                   ajaxResponse.response,
                              );
                         }),
                         catchError((error) => of(ajaxError(error))),
                    );
          }),
     );
};

export const retailSalesGetDealersEpic = (actions$) => {
     return actions$.pipe(
          ofType(retailSalesConstants.RETAIL_SALES_GET_DEALERS_REQUEST),
          switchMap((action) => {
               return ajax
                    .post(
                         "/api/retailSales/getDealers",
                         action.payload,
                         getRequestHeader(requestTypes.REQUEST_TYPE_POST),
                    )
                    .pipe(
                         map((ajaxResponse) => {
                              return retailSalesGetDealersComplete(
                                   ajaxResponse.response,
                              );
                         }),
                         catchError((error) => of(ajaxError(error))),
                    );
          }),
     );
};

export const retailSalesSaveDealersEpic = (actions$) => {
     return actions$.pipe(
          ofType(retailSalesConstants.RETAIL_SALES_SAVE_DEALERS_REQUEST),
          mergeMap((action) => {
               return ajax
                    .post(
                         "/api/retailSales/saveDealers",
                         action.payload,
                         getRequestHeader(requestTypes.REQUEST_TYPE_POST),
                    )
                    .pipe(
                         map((ajaxResponse) => {
                              return retailSalesSaveDealersComplete(
                                   ajaxResponse.response,
                              );
                         }),
                         catchError((error) => of(ajaxError(error))),
                    );
          }),
     );
};

export const retailSalesDealerLookupEpic = (actions$) => {
     return actions$.pipe(
          ofType(retailSalesConstants.RETAIL_SALES_DEALER_LOOKUP_REQUEST),
          switchMap((action) => {
               return ajax
                    .post(
                         "/api/retailSales/dealerLookup",
                         action.payload,
                         getRequestHeader(requestTypes.REQUEST_TYPE_POST),
                    )
                    .pipe(
                         map((ajaxResponse) => {
                              return retailSalesDealerLookupComplete(
                                   ajaxResponse.response,
                              );
                         }),
                         catchError((error) => of(ajaxError(error))),
                    );
          }),
     );
};

export const retailSalesDealerListEpic = (actions$) => {
     return actions$.pipe(
          ofType(retailSalesConstants.RETAIL_SALES_DEALER_LIST_REQUEST),
          switchMap((action) => {
               return ajax
                    .post(
                         "/api/retailSales/dealerList",
                         action.payload,
                         getRequestHeader(requestTypes.REQUEST_TYPE_POST),
                    )
                    .pipe(
                         map((ajaxResponse) => {
                              return retailSalesDealerListComplete(
                                   ajaxResponse.response,
                              );
                         }),
                         catchError((error) => of(ajaxError(error))),
                    );
          }),
     );
};

export const retailSalesGetJobTitlesEpic = (actions$) => {
     return actions$.pipe(
          ofType(retailSalesConstants.RETAIL_SALES_GET_JOB_TITLES_REQUEST),
          switchMap((action) => {
               return ajax
                    .post(
                         "/api/retailSales/getJobTitles",
                         action.payload,
                         getRequestHeader(requestTypes.REQUEST_TYPE_POST),
                    )
                    .pipe(
                         map((ajaxResponse) => {
                              return retailSalesGetJobTitlesComplete(
                                   ajaxResponse.response,
                              );
                         }),
                         catchError((error) => of(ajaxError(error))),
                    );
          }),
     );
};

export const retailSalesSaveJobTitlesEpic = (actions$) => {
     return actions$.pipe(
          ofType(retailSalesConstants.RETAIL_SALES_SAVE_JOB_TITLES_REQUEST),
          mergeMap((action) => {
               return ajax
                    .post(
                         "/api/retailSales/saveJobTitles",
                         action.payload,
                         getRequestHeader(requestTypes.REQUEST_TYPE_POST),
                    )
                    .pipe(
                         map((ajaxResponse) => {
                              return retailSalesSaveJobTitlesComplete(
                                   ajaxResponse.response,
                              );
                         }),
                         catchError((error) => of(ajaxError(error))),
                    );
          }),
     );
};

export const retailSalesDeleteJobTitleEpic = (actions$) => {
     return actions$.pipe(
          ofType(retailSalesConstants.RETAIL_SALES_DELETE_JOB_TITLE_REQUEST),
          mergeMap((action) => {
               return ajax
                    .post(
                         "/api/retailSales/deleteJobTitle",
                         action.payload,
                         getRequestHeader(requestTypes.REQUEST_TYPE_POST),
                    )
                    .pipe(
                         map((ajaxResponse) => {
                              return retailSalesDeleteJobTitleComplete(
                                   ajaxResponse.response,
                              );
                         }),
                         catchError((error) => of(ajaxError(error))),
                    );
          }),
     );
};
